// Pure derivation helpers for the Knowledge Quality drawer.
import { formatPercent } from "../../utils";
import {
  severityRank,
  STRONG_KNOWLEDGE_FINDING,
} from "../knowledgeQualityIntelligenceHelpers";
import type {
  KQCompleteness,
  KQEvidence,
  KQFinding,
  KQRichness,
  KnowledgeQualityResult,
} from "../knowledgeQualityTypes";
import { KQ_BANNER } from "./constants";

export interface KQEntityRow {
  entity: string;
  completeness: number | null;
  required: number;
  available: number;
  missing: string[];
  richness: number | null;
  relatedConcepts: string[];
  trust: number | null;
  strength: KQEvidence["evidence_strength"] | null;
  source: string;
}

export interface KQDerived {
  summary: KnowledgeQualityResult["summary"];
  completeness: KQCompleteness[];
  knowledgeRichness: KQRichness[];
  evidenceTrust: KQEvidence[];
  qualityFindings: KQFinding[];
  sortedFindings: KQFinding[];
  entitiesMerged: KQEntityRow[];
  overallPct: string;
  richnessPct: string;
  trustPct: string;
  strongCount: number;
  gapsCount: number;
  topGap: KQCompleteness | undefined;
  missingAttrs: number;
  missingAttrsBannerText: string;
  hasMissingAttrsBanner: boolean;
  docId: string;
}

export function mergeEntityCoverage(
  completeness: KQCompleteness[],
  richness: KQRichness[],
  evidence: KQEvidence[],
): KQEntityRow[] {
  const richMap = new Map(richness.map((r) => [r.knowledge_entity, r]));
  const evidenceMap = new Map(evidence.map((e) => [e.knowledge_entity, e]));
  return completeness.map((c) => {
    const rich = richMap.get(c.knowledge_entity);
    const ev = evidenceMap.get(c.knowledge_entity);
    return {
      entity: c.knowledge_entity,
      completeness: c.completeness_score,
      required: (c.required_attributes ?? []).length,
      available: (c.available_attributes ?? []).length,
      missing: c.missing_attributes ?? [],
      richness: rich?.knowledge_richness_score ?? null,
      relatedConcepts: rich?.related_concepts ?? [],
      trust: ev?.trust_score ?? null,
      strength: ev?.evidence_strength ?? null,
      source: (c.source_location ?? []).join(", "),
    };
  });
}

export function sortFindingsByGapThenSeverity(rows: KQFinding[]): KQFinding[] {
  return [...rows].sort((a, b) => {
    const aGap = a.finding_type === STRONG_KNOWLEDGE_FINDING ? 1 : 0;
    const bGap = b.finding_type === STRONG_KNOWLEDGE_FINDING ? 1 : 0;
    if (aGap !== bGap) return aGap - bGap;
    return severityRank(a.severity) - severityRank(b.severity);
  });
}

export function countStrongFindings(rows: KQFinding[]): number {
  return rows.filter((f) => f.finding_type === STRONG_KNOWLEDGE_FINDING).length;
}

export function findTopGap(
  rows: KQCompleteness[],
): KQCompleteness | undefined {
  return rows.find((c) => (c.completeness_score ?? 1) < 1);
}

export function buildMissingAttrsBannerText(
  topGap: KQCompleteness | undefined,
): string {
  const detail = topGap
    ? `"${topGap.knowledge_entity}" is missing: ${(topGap.missing_attributes ?? []).join(", ")}.`
    : KQ_BANNER.missingAttrsFallback;
  return `${detail} ${KQ_BANNER.missingAttrsHelp}`;
}

export function pluralize(
  count: number,
  singular: string,
  plural: string,
): string {
  return count === 1 ? singular : plural;
}

export function deriveKnowledgeIntel(
  data: KnowledgeQualityResult,
): KQDerived {
  const summary = data.summary ?? ({} as KnowledgeQualityResult["summary"]);
  const completeness = data.completeness ?? [];
  const knowledgeRichness = data.knowledge_richness ?? [];
  const evidenceTrust = data.evidence_trust ?? [];
  const qualityFindings = data.knowledge_quality_findings ?? [];
  const entitiesMerged = mergeEntityCoverage(
    completeness,
    knowledgeRichness,
    evidenceTrust,
  );
  const strongCount = countStrongFindings(qualityFindings);
  const topGap = findTopGap(completeness);
  const missingAttrs = summary.missing_attributes ?? 0;

  return {
    summary,
    completeness,
    knowledgeRichness,
    evidenceTrust,
    qualityFindings,
    sortedFindings: sortFindingsByGapThenSeverity(qualityFindings),
    entitiesMerged,
    overallPct: formatPercent(summary.average_completeness_score),
    richnessPct: formatPercent(summary.average_knowledge_richness_score),
    trustPct: formatPercent(summary.average_trust_score),
    strongCount,
    gapsCount: qualityFindings.length - strongCount,
    topGap,
    missingAttrs,
    missingAttrsBannerText: buildMissingAttrsBannerText(topGap),
    hasMissingAttrsBanner: missingAttrs > 0,
    docId: data.document?.document_id ?? "",
  };
}
